import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/api";

const links = [
  { label: "Servizi", href: "#servizi" },
  { label: "Lavori", href: "#lavori" },
  { label: "Chi Siamo", href: "#chi-siamo" },
  { label: "Clienti Soddisfatti", href: "/clienti-soddisfatti", page: true },
  { label: "Contatti", href: "#contatti" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href, page = false) => {
    setOpen(false);
    if (page) navigate(href);
    else scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-ink/95 backdrop-blur-md border-white/10" : "bg-ink/60 backdrop-blur-sm border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <button onClick={() => go("#top")} className="flex items-center group" data-testid="nav-logo">
          <img
            src="/logo-dark.png"
            alt="Star Edil srls — Costruiamo il futuro, dalle fondamenta"
            className="h-14 md:h-16 w-auto transition-transform duration-500 group-hover:scale-105"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href, l.page)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-oswald text-sm uppercase tracking-[0.2em] text-muted-foreground hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#preventivo")}
            data-testid="nav-cta-quote"
            className="btn-tracer bg-gold text-ink font-oswald uppercase tracking-[0.15em] text-sm px-7 py-3 hover:bg-gold-hover transition-colors duration-300"
          >
            Preventivo
          </button>
        </nav>

        <button
          className="lg:hidden text-foreground"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-ink border-t border-white/10 px-6 py-8 flex flex-col gap-6" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href, l.page)}
              data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-left font-oswald text-lg uppercase tracking-[0.2em] text-muted-foreground hover:text-gold transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#preventivo")}
            data-testid="nav-mobile-cta-quote"
            className="bg-gold text-ink font-oswald uppercase tracking-[0.15em] px-7 py-4 w-fit"
          >
            Richiedi Preventivo
          </button>
        </div>
      )}
    </motion.header>
  );
};
